import { useMutation } from "@tanstack/react-query";
import { useState, type SyntheticEvent } from "react";

import type { BillingOverview, BillingTerms } from "../../shared/billing-schemas";
import { Button } from "../components/ui/button";
import { FieldError } from "../components/ui/field";
import {
  COLLECT_KIND_LABEL,
  COLLECT_SUBMIT_LABEL,
  collectGuide,
  lineIsOptional,
  linesFor,
  offeredKinds,
  submitCollectDraft,
  type CollectDraft,
  type CollectKind,
} from "./billing-collect-request";
import { CheckboxField, SelectField, TextField } from "./billing-fields";
import { cadenceLabel, formatDay, formatUsd } from "./billing-format";
import { fieldState, useFormErrors, type FormErrors } from "./billing-validation";
import { Sheet, SheetBody, SheetFooter, SheetForm, SheetHeader } from "../components/ui/sheet";

/**
 * Turning approved terms into money (ADR-0073): through Stripe, or recorded from elsewhere. One
 * action at a time, in a side sheet opened from the terms' card; the rules live in
 * `billing-collect-request.ts`.
 */

type Update = (patch: Partial<CollectDraft>) => void;

type FieldsProps = {
  draft: CollectDraft;
  update: Update;
  errors: FormErrors;
  requirements: Record<string, string>;
};

function SourceField({ draft, update, errors, requirements }: FieldsProps) {
  return (
    <SelectField
      {...fieldState(errors, requirements, "source", "collect-source")}
      label="Source"
      value={draft.source}
      options={[
        { value: "external", label: "Another invoicing system" },
        { value: "manual", label: "By hand" },
      ]}
      onChange={(source) => {
        update({ source: source === "manual" ? "manual" : "external" });
      }}
    />
  );
}

function ManualInvoiceFields(props: FieldsProps) {
  const { draft, update, errors, requirements } = props;
  const state = (name: string) => fieldState(errors, requirements, name, `collect-${name}`);
  return (
    <div className="grid gap-4 sm:grid-cols-2">
      <TextField
        {...state("description")}
        className="sm:col-span-2"
        label="Description"
        value={draft.description}
        required
        maxLength={200}
        onChange={(description) => {
          update({ description });
        }}
      />
      <TextField
        {...state("amount")}
        label="Amount due (USD)"
        value={draft.amount}
        required
        inputMode="decimal"
        onChange={(amount) => {
          update({ amount });
        }}
      />
      <TextField
        {...state("reference")}
        label="Reference (optional)"
        value={draft.reference}
        maxLength={120}
        onChange={(reference) => {
          update({ reference });
        }}
      />
      <TextField
        {...state("issued")}
        label="Issued on"
        type="date"
        value={draft.issued}
        required
        onChange={(issued) => {
          update({ issued });
        }}
      />
      <TextField
        {...state("due")}
        label="Due on (optional)"
        type="date"
        value={draft.due}
        onChange={(due) => {
          update({ due });
        }}
      />
      <TextField
        {...state("paid")}
        label="Paid on (optional)"
        type="date"
        value={draft.paid}
        onChange={(paid) => {
          update({ paid });
        }}
      />
    </div>
  );
}

function ManualSubscriptionFields({ draft, update, errors, requirements }: FieldsProps) {
  return (
    <div className="grid gap-4 sm:grid-cols-2">
      <TextField
        {...fieldState(errors, requirements, "issued", "collect-started")}
        label="Started on"
        type="date"
        value={draft.issued}
        required
        onChange={(issued) => {
          update({ issued });
        }}
      />
      <TextField
        {...fieldState(errors, requirements, "reference", "collect-subscription-reference")}
        label="Reference (optional)"
        value={draft.reference}
        maxLength={120}
        onChange={(reference) => {
          update({ reference });
        }}
      />
    </div>
  );
}

function ManualFields(props: FieldsProps) {
  if (props.draft.kind === "manual_invoice") {
    return (
      <>
        <SourceField {...props} />
        <ManualInvoiceFields {...props} />
      </>
    );
  }
  if (props.draft.kind === "manual_subscription") {
    return (
      <>
        <SourceField {...props} />
        <ManualSubscriptionFields {...props} />
      </>
    );
  }
  return null;
}

function LinePicker({
  draft,
  lines,
  update,
  errors,
  requirements,
}: FieldsProps & { lines: BillingTerms["lines"] }) {
  const optional = lineIsOptional(draft.kind);
  const unset = optional
    ? [
        {
          value: "",
          label:
            draft.kind === "stripe_invoice"
              ? "Every one-time charge on one invoice"
              : "Not tied to one line",
        },
      ]
    : [];
  return (
    <SelectField
      {...fieldState(errors, requirements, "line", "collect-line")}
      label={optional ? "Line (optional)" : "Line"}
      value={draft.lineId}
      options={[
        ...unset,
        ...lines.map((line) => ({
          value: line.id,
          label: `${line.description} · ${formatUsd(line.totalMinor)} ${
            line.kind === "credit" ? "credit" : cadenceLabel(line.cadence)
          }`,
        })),
      ]}
      onChange={(lineId) => {
        update({ lineId });
      }}
    />
  );
}

function ActionPicker({
  draft,
  kinds,
  update,
  errors,
  requirements,
}: FieldsProps & { kinds: CollectKind[] }) {
  return (
    <SelectField
      {...fieldState(errors, requirements, "kind", "collect-kind")}
      label="Action"
      value={draft.kind}
      options={kinds.map((kind) => ({ value: kind, label: COLLECT_KIND_LABEL[kind] }))}
      onChange={(kind) => {
        update({ kind: kind as CollectKind, lineId: "" });
      }}
    />
  );
}

function CollectFields(props: FieldsProps & { lines: BillingTerms["lines"] }) {
  const { draft, update, errors, requirements } = props;
  const showDays = draft.kind === "stripe_invoice" || draft.kind === "stripe_subscription";
  return (
    <>
      <LinePicker {...props} />
      {showDays ? (
        <TextField
          {...fieldState(errors, requirements, "days", "collect-days")}
          label="Days until due"
          value={draft.days}
          required
          inputMode="numeric"
          onChange={(days) => {
            update({ days });
          }}
        />
      ) : null}
      {showDays ? (
        <CheckboxField
          id="collect-allow-card"
          label="Also accept card"
          hint="ACH bank debit is always offered and costs 0.8%, at most $5. A card costs 2.9% + 30¢."
          checked={draft.allowCard}
          onChange={(allowCard) => {
            update({ allowCard });
          }}
        />
      ) : null}
      {draft.kind === "stripe_subscription" ? (
        <TextField
          {...fieldState(errors, requirements, "goLive", "collect-go-live")}
          label="First invoice on (go-live)"
          type="date"
          value={draft.goLive ?? ""}
          onChange={(goLive) => {
            update({ goLive });
          }}
        />
      ) : null}
      <ManualFields {...props} />
      <TextField
        {...fieldState(errors, requirements, "reason", "collect-reason")}
        label="Reason"
        value={draft.reason}
        required
        maxLength={500}
        onChange={(reason) => {
          update({ reason });
        }}
      />
    </>
  );
}

/**
 * The draft as the pickers show it. The form mounts with the tab, often before any terms are
 * approved or Stripe is linked, so a stored choice may name terms or an action that no longer
 * (or does not yet) exist; a `<select>` with such a value displays its first option, and that is
 * what the employee believes they are submitting.
 */
function resolveDraft(
  draft: CollectDraft,
  kinds: CollectKind[],
  approved: BillingTerms[],
): CollectDraft {
  const kind = kinds.includes(draft.kind) ? draft.kind : (kinds[0] ?? draft.kind);
  const termsId = approved.some((terms) => terms.id === draft.termsId)
    ? draft.termsId
    : (approved[0]?.id ?? "");
  const terms = approved.find((candidate) => candidate.id === termsId);
  return { ...draft, kind, termsId, goLive: draft.goLive ?? upcomingDay(terms?.effectiveAt) };
}

/**
 * Terms that take effect later suggest their start as the go-live day, so a subscription signed
 * now bills from then; terms already in effect suggest nothing, which invoices today.
 */
function upcomingDay(effectiveAt: number | undefined): string {
  return effectiveAt !== undefined && effectiveAt * 1_000 > Date.now()
    ? formatDay(effectiveAt)
    : "";
}

function initialDraft(kind: CollectKind, termsId: string): CollectDraft {
  return {
    kind,
    termsId,
    lineId: "",
    days: "30",
    description: "",
    amount: "",
    reference: "",
    issued: "",
    due: "",
    paid: "",
    source: "external",
    goLive: null,
    allowCard: false,
    reason: "",
  };
}

/** Draft keys whose field on screen goes by another name. */
const DRAFT_FIELD: Partial<Record<keyof CollectDraft, string>> = {
  termsId: "terms",
  lineId: "line",
};

const SHEET_TITLE: Record<CollectKind, string> = {
  stripe_invoice: "Send an invoice",
  stripe_subscription: "Start billing",
  stripe_credit: "Apply a credit",
  manual_invoice: "Record an outside invoice",
  manual_subscription: "Record an outside subscription",
  manual_credit: "Record an outside credit",
};

/** What the deal card asks for: which terms, and which action to start from. */
export type CollectTarget = { termsId: string; kind: CollectKind };

type PanelProps = {
  customerSlug: string;
  billing: BillingOverview;
  canManage: boolean;
  canCollect: boolean;
  target: CollectTarget;
  onClose: () => void;
  onChanged: () => Promise<void>;
};

function CollectPanel({
  customerSlug,
  billing,
  canManage,
  canCollect,
  target,
  onClose,
  onChanged,
}: PanelProps) {
  const approved = billing.terms.filter((terms) => terms.status === "approved");
  const kinds = offeredKinds(billing, canManage, canCollect);
  const [stored, setDraft] = useState<CollectDraft>(() =>
    initialDraft(target.kind, target.termsId),
  );
  const draft = resolveDraft(stored, kinds, approved);
  const guide = collectGuide(draft.kind);
  const errors = useFormErrors(guide);
  const update: Update = (patch) => {
    for (const key of Object.keys(patch)) {
      errors.clear(DRAFT_FIELD[key as keyof CollectDraft] ?? key);
    }
    setDraft((current) => ({ ...current, ...patch }));
  };
  const terms = approved.find((candidate) => candidate.id === draft.termsId);
  const lines = linesFor(terms, draft.kind);
  const submit = useMutation({
    mutationFn: () => submitCollectDraft(customerSlug, draft, lines),
    onSuccess: async () => {
      await onChanged();
      onClose();
    },
    onError: errors.fail,
  });
  function onSubmit(event: SyntheticEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!lineIsOptional(draft.kind) && lines.length === 0) {
      errors.fail(new Error("These terms have no line of that kind."));
      return;
    }
    submit.mutate();
  }
  const fields = { draft, update, errors, requirements: guide.requirements };
  return (
    <>
      <SheetHeader
        title={SHEET_TITLE[draft.kind]}
        kicker={terms ? `${terms.title} · ${terms.tool?.displayName ?? "Whole organization"}` : ""}
        description="Stripe emails the customer a hosted invoice; an outside record creates nothing in Stripe."
      />
      <SheetForm onSubmit={onSubmit} aria-label="Collect or record" noValidate>
        <SheetBody>
          <ActionPicker {...fields} kinds={kinds} />
          <CollectFields {...fields} lines={lines} />
          {errors.summary ? <FieldError>{errors.summary}</FieldError> : null}
        </SheetBody>
        <SheetFooter>
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={submit.isPending}>
            {COLLECT_SUBMIT_LABEL[draft.kind]}
          </Button>
        </SheetFooter>
      </SheetForm>
    </>
  );
}

/**
 * Turning one set of approved terms into money, opened from its card (ADR-0087): a side sheet,
 * starting from the action the card offered, that can switch to any action the employee may take.
 */
export function BillingCollectSheet(
  props: Omit<PanelProps, "target"> & { target: CollectTarget | null },
) {
  const { target, onClose } = props;
  return (
    <Sheet open={target !== null} onClose={onClose}>
      {target ? (
        <CollectPanel key={`${target.termsId}-${target.kind}`} {...props} target={target} />
      ) : null}
    </Sheet>
  );
}
